import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import AdminSidebar from "./AdminSidebar";
import AdminTopHeader from "./AdminTopHeader";
import AdminFooter from "./AdminFooter";

const COLLAPSE_KEY = "rythugaru-admin-sidebar-collapsed";

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(COLLAPSE_KEY) === "1",
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingBookings, setPendingBookings] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    }
  }, [collapsed]);

  const loadBadges = useCallback(async () => {
    try {
      const [bookingsRes, reviewsRes] = await Promise.all([
        supabase.from("bookings").select("id", { count: "exact", head: true }).eq("status", "new"),
        supabase.from("reviews").select("id", { count: "exact", head: true }).eq("status", "pending"),
      ]);
      setPendingBookings(bookingsRes.count || 0);
      setPendingReviews(reviewsRes.count || 0);
    } catch {
      // Non-critical — the shell still renders without live badge counts.
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadBadges();
    const refresh = () => loadBadges();
    window.addEventListener("admin-badges-refresh", refresh);
    return () => window.removeEventListener("admin-badges-refresh", refresh);
  }, [user, loadBadges]);

  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 3200);
  }, []);

  const handleToggleSidebar = () => {
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches) {
      setCollapsed((value) => !value);
    } else {
      setMobileOpen((value) => !value);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // The auth listener clears local session state regardless.
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center">
        <meta name="robots" content="noindex, nofollow" />
        <span className="flex flex-col items-center gap-3 text-foreground-600">
          <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
          <span className="text-sm">Loading the admin portal…</span>
        </span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background-100">
      <meta name="robots" content="noindex, nofollow" />
      <AdminSidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        pendingBookings={pendingBookings}
        pendingReviews={pendingReviews}
        onCloseMobile={() => setMobileOpen(false)}
        onToggleCollapse={() => setCollapsed((value) => !value)}
        onSignOut={handleSignOut}
      />

      <div className={collapsed ? "lg:pl-[78px]" : "lg:pl-[272px]"}>
        <AdminTopHeader
          email={user.email}
          onToggleSidebar={handleToggleSidebar}
          onSignOut={handleSignOut}
          onNotify={notify}
          pendingReviews={pendingReviews}
        />

        <main className="min-h-[calc(100vh-8rem)] px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <Outlet />
        </main>

        <AdminFooter />
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] max-w-[90vw]">
          <div className="flex items-center gap-3 bg-foreground-950 text-background-50 text-sm px-4 py-3 rounded-lg">
            <i className="ri-information-line text-accent-400 text-lg"></i>
            <span>{toast}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}